import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getSnapshot, deleteListing, getDrafts,
  saveListingDraft, deleteListingDraft, deleteImageDraft,
  generateListing, getTargetLocales, setTargetLocales,
  getLocJob, cancelLocJob,
  type PlayApp, type PlayListing, type GeneratedListing,
} from '@/api/play';
import { sourceScreenshots } from '@/lib/playScreenshots';
import { playLocaleName } from '@/lib/playLocales';
import { isRtlLocale } from '@/lib/rtlLocales';
import { Button } from '../Button';
import { ReviewNotice } from './ReviewNotice';
import { ConfirmModal } from './ConfirmModal';
import { ManageLocalesModal } from './ManageLocalesModal';
import { ImagesPanel } from './ImagesPanel';
import { SubmitDraftsModal } from './SubmitDraftsModal';
import { DraftHistoryModal } from './DraftHistoryModal';
import { BulkLocalizeModal } from './BulkLocalizeModal';
import { LocJobBanner } from './LocJobBanner';
import { WizardPackagePanel } from './WizardPackagePanel';

const LIMITS = { title: 30, shortDescription: 80, fullDescription: 4000 };

function Counter({ value, max }: { value: string; max: number }) {
  const over = value.length > max;
  return (
    <span className={`text-xs ${over ? 'text-red-400' : 'text-content-muted'}`}>
      {value.length}/{max}
    </span>
  );
}

const emptyListing = (lang: string): PlayListing => ({
  language: lang, title: '', shortDescription: '', fullDescription: '', video: '',
});

function sameListing(a: PlayListing, b: PlayListing): boolean {
  return a.title === b.title
    && a.shortDescription === b.shortDescription
    && a.fullDescription === b.fullDescription
    && a.video === b.video;
}

// Draft-first editor: the form shows the server draft when one exists, else
// the live listing. "Save draft" writes to D1 only; nothing reaches Google
// until SubmitDraftsModal commits the chosen locales.
export function ListingEditor({ app }: { app: PlayApp }) {
  const qc = useQueryClient();
  const { data: snapshot, isLoading: liveLoading, error: loadError, refetch } = useQuery({
    queryKey: ['play-snapshot', app.id],
    queryFn: () => getSnapshot(app.id),
  });
  const listings = snapshot?.listings;
  const { data: drafts, isLoading: draftsLoading } = useQuery({
    queryKey: ['play-drafts', app.id],
    queryFn: () => getDrafts(app.id),
  });

  // Unsaved local edits, keyed by language; survive chip switches.
  const [edits, setEdits] = useState<Record<string, PlayListing>>({});
  const [activeLang, setActiveLang] = useState<string | null>(null);
  const [showSubmit, setShowSubmit] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [confirm, setConfirm] = useState<'discard' | 'delete' | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [showManage, setShowManage] = useState(false);
  const [sourceLang, setSourceLang] = useState<string>('');
  const [bulkProgress, setBulkProgress] = useState<{ done: number; total: number } | null>(null);
  const [showBulk, setShowBulk] = useState(false);

  // Default the generation source: en-US if present, else first en-*, else first filled locale.
  useEffect(() => {
    if (sourceLang) return;
    const filled = (listings ?? []).map((l) => l.language);
    const pick = filled.find((l) => l === 'en-US') ?? filled.find((l) => l.startsWith('en')) ?? filled[0];
    if (pick) setSourceLang(pick);
  }, [listings, sourceLang]);

  const live = useMemo(
    () => new Map((listings ?? []).map((l) => [l.language, l])),
    [listings],
  );
  const draftListings = useMemo(
    () => new Map((drafts?.listings ?? []).map((l) => [l.language, l])),
    [drafts],
  );
  const imageDraftLangs = useMemo(
    () => new Set((drafts?.images ?? []).map((d) => d.language)),
    [drafts],
  );

  // Background localization run. The job lives in D1, so this restores the
  // banner (progress or final summary) even after the tab was closed.
  const { data: locJob } = useQuery({
    queryKey: ['play-loc-job', app.id],
    queryFn: () => getLocJob(app.id),
    refetchInterval: (q) => {
      const st = q.state.data?.job?.status;
      return st === 'pending' || st === 'running' ? 5000 : false;
    },
  });
  const locActive = locJob?.job?.status === 'pending' || locJob?.job?.status === 'running';
  const overLimitLangs = useMemo(() => new Set(locJob?.overLimitLangs ?? []), [locJob]);

  // A finished run wrote drafts straight to D1 — pull them in so the board
  // repaints without a manual reload.
  const locFinishedAt = locJob?.job?.finishedAt ?? null;
  useEffect(() => {
    if (locFinishedAt) qc.invalidateQueries({ queryKey: ['play-drafts', app.id] });
  }, [locFinishedAt, qc, app.id]);

  const cancelLocMut = useMutation({
    mutationFn: (jobId: string) => cancelLocJob(app.id, jobId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['play-loc-job', app.id] }),
    onError: (e: any) => setActionError(e?.message ?? 'Failed to cancel the run'),
  });

  const { data: targetData } = useQuery({
    queryKey: ['play-target-locales', app.id],
    queryFn: () => getTargetLocales(app.id),
  });
  const targetLocales = targetData?.locales ?? [];

  const langs = useMemo(() => {
    const withContent = new Set<string>([
      ...(listings ?? []).map((l) => l.language),
      ...draftListings.keys(),
      ...imageDraftLangs,
      ...Object.keys(edits),
    ]);
    // Board = target set first (its order), then any content locale not in it.
    const inTarget = new Set(targetLocales.map((l) => l));
    const extras = [...withContent].filter((l) => !inTarget.has(l)).sort();
    return [...targetLocales, ...extras];
  }, [targetLocales, listings, draftListings, imageDraftLangs, edits]);

  useEffect(() => {
    if (activeLang === null && langs.length > 0) setActiveLang(langs[0]);
    else if (activeLang !== null && !langs.includes(activeLang) && langs.length > 0) setActiveLang(langs[0]);
    else if (activeLang !== null && langs.length === 0) setActiveLang(null);
  }, [langs, activeLang]);

  // Base = server draft ?? live ?? empty. Local edits layer on top of it.
  const baseFor = (lang: string): PlayListing => {
    const d = draftListings.get(lang);
    if (d) {
      return {
        language: lang, title: d.title, shortDescription: d.shortDescription,
        fullDescription: d.fullDescription, video: d.video,
      };
    }
    return live.get(lang) ?? emptyListing(lang);
  };

  const formFor = (lang: string): PlayListing => edits[lang] ?? baseFor(lang);

  const hasUnsaved = (lang: string): boolean => {
    const e = edits[lang];
    return !!e && !sameListing(e, baseFor(lang));
  };

  const hasServerDraft = (lang: string): boolean =>
    draftListings.has(lang) || imageDraftLangs.has(lang);

  const isEmptyLocale = (lang: string): boolean => {
    const f = formFor(lang);
    return !f.title && !f.shortDescription && !f.fullDescription
      && !live.has(lang) && !draftListings.has(lang);
  };

  const sourceListing = (): PlayListing | null => {
    if (!sourceLang) return null;
    return baseFor(sourceLang);
  };

  const isDraftOnly = (lang: string) => !live.has(lang);

  const dropEdit = (lang: string) =>
    setEdits((prev) => {
      const next = { ...prev };
      delete next[lang];
      return next;
    });

  const active = activeLang ? formFor(activeLang) : null;

  const setField = (patch: Partial<PlayListing>) => {
    if (!activeLang) return;
    setEdits((prev) => ({ ...prev, [activeLang]: { ...formFor(activeLang), ...patch } }));
  };

  const invalidateDrafts = () => qc.invalidateQueries({ queryKey: ['play-drafts', app.id] });

  const saveDraftMut = useMutation({
    mutationFn: (l: PlayListing) => saveListingDraft(app.id, l.language, {
      title: l.title, shortDescription: l.shortDescription,
      fullDescription: l.fullDescription, video: l.video,
    }),
    onSuccess: (_d, l) => {
      dropEdit(l.language);
      invalidateDrafts();
      setActionError(null);
    },
    onError: (e: any) => setActionError(e?.message ?? 'Failed to save draft'),
  });

  const discardDraftsFor = async (lang: string) => {
    if (draftListings.has(lang)) await deleteListingDraft(app.id, lang);
    for (const d of drafts?.images ?? []) {
      if (d.language === lang) await deleteImageDraft(app.id, lang, d.imageType);
    }
  };

  const discardMut = useMutation({
    mutationFn: (lang: string) => discardDraftsFor(lang),
    onSuccess: (_d, lang) => {
      dropEdit(lang);
      invalidateDrafts();
      setConfirm(null);
      setActionError(null);
    },
    onError: (e: any) => setActionError(e?.message ?? 'Failed to discard draft'),
  });

  const deleteLocaleMut = useMutation({
    mutationFn: async (lang: string) => {
      await deleteListing(app.id, lang);
      await discardDraftsFor(lang);
    },
    onSuccess: (_d, lang) => {
      dropEdit(lang);
      qc.invalidateQueries({ queryKey: ['play-snapshot', app.id] });
      invalidateDrafts();
      setConfirm(null);
      setActionError(null);
    },
    onError: (e: any) => setActionError(e?.message ?? 'Failed to delete locale'),
  });

  // Transcreate one locale from the source. Saves a draft when it fits Play
  // limits, else drops it into local edits with an over-limit notice.
  const applyGenerated = (lang: string, g: GeneratedListing, video: string) => {
    const next: PlayListing = {
      language: lang, title: g.title, shortDescription: g.shortDescription,
      fullDescription: g.fullDescription, video,
    };
    if (g.fits) {
      saveDraftMut.mutate(next);
    } else {
      setEdits((prev) => ({ ...prev, [lang]: next }));
      setActionError('Generated text exceeds Play limits — trim the highlighted fields and save the draft.');
    }
  };

  const generateMut = useMutation({
    mutationFn: ({ lang }: { lang: string }) => {
      const src = sourceListing();
      if (!src) throw new Error('Pick a source locale with content first');
      if (!src.title && !src.shortDescription && !src.fullDescription) {
        throw new Error('The source locale is empty — fill it first');
      }
      return generateListing({
        sourceLang: src.language, targetLang: lang,
        title: src.title, shortDescription: src.shortDescription, fullDescription: src.fullDescription,
      }).then((g) => ({ lang, g, video: baseFor(lang).video }));
    },
    onSuccess: ({ lang, g, video }) => applyGenerated(lang, g, video),
    onError: (e: any) => setActionError(e?.message ?? 'Generation failed'),
  });

  // Sequential so a per-locale failure never aborts the rest; each result is
  // saved as a draft (or parked in edits if over-limit) as it arrives.
  const generateAllEmpty = async () => {
    const src = sourceListing();
    if (!src || (!src.title && !src.shortDescription && !src.fullDescription)) {
      setActionError('Pick a source locale with content first');
      return;
    }
    const targets = langs.filter((l) => l !== src.language && isEmptyLocale(l));
    setBulkProgress({ done: 0, total: targets.length });
    for (let i = 0; i < targets.length; i++) {
      const lang = targets[i];
      try {
        const g = await generateListing({
          sourceLang: src.language, targetLang: lang,
          title: src.title, shortDescription: src.shortDescription, fullDescription: src.fullDescription,
        });
        applyGenerated(lang, g, baseFor(lang).video);
      } catch (e: any) {
        setActionError(`Failed on ${lang}: ${e?.message ?? 'error'}`);
      }
      setBulkProgress({ done: i + 1, total: targets.length });
    }
    setBulkProgress(null);
  };

  const saveTargetsMut = useMutation({
    mutationFn: (locales: string[]) => setTargetLocales(app.id, locales),
    onSuccess: () => {
      setShowManage(false);
      qc.invalidateQueries({ queryKey: ['play-target-locales', app.id] });
    },
    onError: (e: any) => setActionError(e?.message ?? 'Failed to save locales'),
  });

  const draftCount = (drafts?.listings.length ?? 0) + (drafts?.images.length ?? 0);

  if (liveLoading || draftsLoading) return <p className="text-content-muted text-sm">Loading listing…</p>;
  if (loadError) {
    return (
      <div className="bg-surface border border-border rounded-lg p-6 text-center">
        <p className="text-sm text-red-400 mb-3">{(loadError as Error).message}</p>
        <Button variant="secondary" size="sm" onClick={() => refetch()}>Retry</Button>
      </div>
    );
  }

  const overLimit = !!active && (
    active.title.length > LIMITS.title
    || active.shortDescription.length > LIMITS.shortDescription
    || active.fullDescription.length > LIMITS.fullDescription
  );

  const canSaveActive = !!active && !!activeLang && hasUnsaved(activeLang) && !overLimit;

  return (
    <div className="space-y-4">
      <ReviewNotice variant="listing" />
      <WizardPackagePanel appId={app.id} />
      {locJob?.job && (
        <LocJobBanner
          status={locJob}
          cancelling={cancelLocMut.isPending}
          onCancel={() => cancelLocMut.mutate(locJob.job!.id)}
        />
      )}
      <div className="bg-surface border border-border rounded-lg p-4">
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <label className="text-xs text-content-muted">Source</label>
          <select
            value={sourceLang}
            onChange={(e) => setSourceLang(e.target.value)}
            className="px-2 py-1 rounded-md bg-background border border-border text-content text-xs"
          >
            {langs.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
          <Button
            size="sm" variant="secondary"
            disabled={!!bulkProgress}
            onClick={generateAllEmpty}
          >
            {bulkProgress ? `Generating ${bulkProgress.done}/${bulkProgress.total}…` : 'Generate all empty'}
          </Button>
          <Button
            size="sm"
            variant="secondary"
            disabled={locActive || !sourceLang}
            title={locActive ? 'A localization run is already in progress' : undefined}
            onClick={() => setShowBulk(true)}
          >
            Localize all…
          </Button>
          <button
            onClick={() => setShowManage(true)}
            className="px-2.5 py-1 rounded-md text-xs border border-dashed border-border-strong text-content-muted hover:text-content"
          >
            Manage locales
          </button>
          <div className="ml-auto flex items-center gap-2">
            <Button size="sm" variant="secondary" onClick={() => setShowHistory(true)}>History</Button>
            <Button size="sm" disabled={draftCount === 0} onClick={() => setShowSubmit(true)}>Submit to review…</Button>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-4">
          {langs.map((lang) => {
            const empty = isEmptyLocale(lang);
            return (
              <button
                key={lang}
                onClick={() => setActiveLang(lang)}
                title={playLocaleName(lang) ?? undefined}
                className={`px-2.5 py-1 rounded-md text-xs border transition-colors ${
                  activeLang === lang
                    ? 'bg-primary text-white border-primary'
                    : empty
                      ? 'bg-background text-content-subtle border-dashed border-border'
                      : 'bg-background text-content-muted border-border hover:text-content'
                }`}
              >
                {lang}
                {hasServerDraft(lang) && (
                  <span className={`ml-1.5 px-1 py-px rounded text-[10px] font-medium ${
                    activeLang === lang ? 'bg-white/20 text-white' : 'bg-amber-500/15 text-amber-400'
                  }`}>draft</span>
                )}
                {hasUnsaved(lang) && (
                  <span className="ml-1.5 inline-block w-1.5 h-1.5 rounded-full bg-amber-400 align-middle" />
                )}
                {/* The background run saves over-limit text (there are no local
                    edits to park it in) — flag it so it gets trimmed by hand. */}
                {overLimitLangs.has(lang) && (
                  <span className="ml-1.5 px-1 py-px rounded text-[10px] font-medium bg-red-500/15 text-red-400">
                    over limit
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {active && activeLang ? (
          <>
            <h3 className="text-sm font-medium text-content mb-3">
              {activeLang}
              <span className="text-content-muted font-normal">
                {' — '}{playLocaleName(activeLang) ?? 'Custom locale'}
                {isDraftOnly(activeLang) ? ' · draft only, not on Google Play yet' : ''}
              </span>
            </h3>

            <div className="flex items-center gap-2 mb-3">
              <Button
                size="sm" variant="secondary"
                loading={generateMut.isPending && generateMut.variables?.lang === activeLang}
                disabled={activeLang === sourceLang}
                onClick={() => activeLang && generateMut.mutate({ lang: activeLang })}
              >
                Generate for {activeLang}
              </Button>
              <span className="text-xs text-content-muted">
                Transcreates from {sourceLang || '—'} (local terms, native script, within limits)
              </span>
            </div>

            <div className="mb-3">
              <div className="flex justify-between">
                <label className="text-sm text-content-muted">Title</label>
                <Counter value={active.title} max={LIMITS.title} />
              </div>
              <input
                dir={isRtlLocale(activeLang) ? 'rtl' : 'ltr'}
                className="w-full px-3 py-2 rounded-md bg-background border border-border text-content text-sm"
                value={active.title}
                onChange={(e) => setField({ title: e.target.value })}
              />
            </div>
            <div className="mb-3">
              <div className="flex justify-between">
                <label className="text-sm text-content-muted">Short description</label>
                <Counter value={active.shortDescription} max={LIMITS.shortDescription} />
              </div>
              <textarea
                dir={isRtlLocale(activeLang) ? 'rtl' : 'ltr'}
                className="w-full px-3 py-2 rounded-md bg-background border border-border text-content text-sm h-16"
                value={active.shortDescription}
                onChange={(e) => setField({ shortDescription: e.target.value })}
              />
            </div>
            <div className="mb-3">
              <div className="flex justify-between">
                <label className="text-sm text-content-muted">Full description</label>
                <Counter value={active.fullDescription} max={LIMITS.fullDescription} />
              </div>
              <textarea
                dir={isRtlLocale(activeLang) ? 'rtl' : 'ltr'}
                className="w-full px-3 py-2 rounded-md bg-background border border-border text-content text-sm h-48"
                value={active.fullDescription}
                onChange={(e) => setField({ fullDescription: e.target.value })}
              />
            </div>
            <div className="mb-3">
              <label className="text-sm text-content-muted block mb-1">Promo video (YouTube URL, optional)</label>
              <input
                className="w-full px-3 py-2 rounded-md bg-background border border-border text-content text-sm"
                value={active.video}
                onChange={(e) => setField({ video: e.target.value })}
              />
            </div>

            <div className="flex items-center justify-between mt-4">
              <div className="flex gap-2">
                {!isDraftOnly(activeLang) && (
                  <Button variant="secondary" size="sm" onClick={() => setConfirm('delete')} disabled={saveDraftMut.isPending}>
                    Delete locale
                  </Button>
                )}
                {hasServerDraft(activeLang) && (
                  <Button variant="secondary" size="sm" onClick={() => setConfirm('discard')} disabled={saveDraftMut.isPending}>
                    Discard draft
                  </Button>
                )}
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs text-content-muted">Saved to draft only — submit separately</span>
                <Button
                  onClick={() => saveDraftMut.mutate(active)}
                  loading={saveDraftMut.isPending}
                  disabled={!canSaveActive}
                >
                  Save draft
                </Button>
              </div>
            </div>
            {actionError && <p className="text-red-400 text-sm mt-2">{actionError}</p>}

            <ImagesPanel appId={app.id} lang={activeLang} />
          </>
        ) : (
          <p className="text-content-muted text-sm">No localizations yet. Add one to get started.</p>
        )}
      </div>

      <ManageLocalesModal
        isOpen={showManage}
        selected={targetLocales}
        saving={saveTargetsMut.isPending}
        onSave={(locales) => saveTargetsMut.mutate(locales)}
        onClose={() => setShowManage(false)}
      />

      {showBulk && (
        <BulkLocalizeModal
          isOpen={showBulk}
          appId={app.id}
          sourceLang={sourceLang}
          langs={langs.filter((l) => l !== sourceLang)}
          emptyLangs={langs.filter((l) => l !== sourceLang && isEmptyLocale(l))}
          sourceItems={sourceScreenshots(snapshot, drafts, sourceLang)}
          sourceText={{
            title: sourceListing()?.title ?? '',
            shortDescription: sourceListing()?.shortDescription ?? '',
            fullDescription: sourceListing()?.fullDescription ?? '',
          }}
          videos={Object.fromEntries(langs.map((l) => [l, baseFor(l).video]))}
          onClose={() => setShowBulk(false)}
          onStarted={() => {
            setShowBulk(false);
            qc.invalidateQueries({ queryKey: ['play-loc-job', app.id] });
          }}
        />
      )}

      {showSubmit && drafts && (
        <SubmitDraftsModal appId={app.id} drafts={drafts} onClose={() => setShowSubmit(false)} />
      )}

      {showHistory && (
        <DraftHistoryModal appId={app.id} onClose={() => setShowHistory(false)} />
      )}

      <ConfirmModal
        isOpen={confirm === 'discard' && !!activeLang}
        title="Discard draft"
        danger
        confirmLabel="Discard"
        loading={discardMut.isPending}
        error={actionError}
        message={`Discard all draft changes for ${activeLang}? Uploaded draft images will be deleted. The live listing on Google Play is not affected.`}
        onConfirm={() => activeLang && discardMut.mutate(activeLang)}
        onClose={() => setConfirm(null)}
      />

      <ConfirmModal
        isOpen={confirm === 'delete' && !!activeLang}
        title="Delete locale"
        danger
        confirmLabel="Delete"
        loading={deleteLocaleMut.isPending}
        error={actionError}
        message={`Delete the ${activeLang} localization from Google Play? Its text and images will be removed from the store listing after Google review. Draft changes for this locale are discarded too.`}
        onConfirm={() => activeLang && deleteLocaleMut.mutate(activeLang)}
        onClose={() => setConfirm(null)}
      />
    </div>
  );
}
